import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Clock, Target, ArrowRight, SkipForward } from "lucide-react";
import { apiMutate } from "@/lib/api";
import { toast } from "sonner";

/**
 * MoveCard — one of today's 3 Growth Moves.
 *
 * Structure driven by the user's Phase 1 requirements:
 * - Icon + Title
 * - Motivational "Why"
 * - Expected Result (concrete outcome)
 * - Estimated time (5/10/15 min chip)
 * - Direct action button → route to feature OR modal (networking log)
 * - Skip is a soft, judgment-free option
 */
export default function MoveCard({ move, index, onChange }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logNote, setLogNote] = useState("");
  const [logCount, setLogCount] = useState(3);

  const isDone = move.status === "done";
  const isSkipped = move.status === "skipped";

  const timeChip = `${move.time_min} min`;

  async function markDone() {
    setBusy(true);
    try {
      const updated = await apiMutate("PATCH", `/today/moves/${move.id}`, { status: "done" });
      toast.success("Move locked in ⚡", { description: move.expected_result });
      onChange?.(updated);
    } catch (e) {
      toast.error("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  }

  async function markSkipped() {
    setBusy(true);
    try {
      const updated = await apiMutate("PATCH", `/today/moves/${move.id}`, { status: "skipped" });
      toast("Skipped — no worries. Tomorrow brings a fresh set.");
      onChange?.(updated);
    } catch (e) {
      toast.error("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  }

  async function submitLog() {
    if (!logCount || logCount < 1) {
      toast.error("Enter a count of at least 1");
      return;
    }
    setBusy(true);
    try {
      await apiMutate("POST", "/networking/log", {
        kind: move.dimension === "networking" ? "networking" : "engagement",
        note: logNote,
        count: Number(logCount),
      });
      await markDone();
      setShowLog(false);
    } catch (e) {
      toast.error("Couldn't log — try again");
    } finally {
      setBusy(false);
    }
  }

  function doAction() {
    if (isDone) return;
    if (move.action?.modal === "networking_log") {
      setShowLog(true);
      return;
    }
    if (move.action?.route) {
      navigate(move.action.route);
    }
  }

  return (
    <div
      data-testid={`move-card-${index}`}
      className="relative rounded-2xl p-5 sm:p-6 flex flex-col gap-4 transition-all"
      style={{
        background: "var(--surface)",
        border: `1px solid ${isDone ? "rgba(216,168,87,0.55)" : "var(--ink-22)"}`,
        boxShadow: isDone ? "0 0 0 1px rgba(216,168,87,0.20) inset" : "none",
        opacity: isSkipped ? 0.55 : 1,
      }}
    >
      {/* Top row: position + icon + status chip */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.24em]"
            style={{ color: "var(--seal)", opacity: 0.85 }}
          >
            Move {index + 1} of 3
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full"
            style={{
              background: "rgba(216,168,87,0.10)",
              border: "1px solid rgba(216,168,87,0.35)",
              color: "var(--seal)",
            }}
          >
            <Clock size={11} strokeWidth={1.8} /> {timeChip}
          </span>
          {isDone && (
            <span
              data-testid={`move-status-done-${index}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full"
              style={{ background: "rgba(80,180,110,0.14)", border: "1px solid rgba(80,180,110,0.45)", color: "#7ac794" }}
            >
              <Check size={11} strokeWidth={2.2} /> Done
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="flex items-start gap-3">
        <div
          className="text-2xl leading-none flex-shrink-0 mt-0.5"
          aria-hidden
          style={{ filter: isDone ? "grayscale(0.4)" : "none" }}
        >
          {move.icon}
        </div>
        <h3
          data-testid={`move-title-${index}`}
          className="font-heading text-lg sm:text-xl leading-tight"
          style={{
            color: "var(--ink)",
            fontVariationSettings: "'wght' 500",
            textDecoration: isDone ? "line-through" : "none",
            textDecorationColor: "rgba(216,168,87,0.55)",
          }}
        >
          {move.title}
        </h3>
      </div>

      {/* Why */}
      <p
        data-testid={`move-why-${index}`}
        className="text-sm leading-relaxed"
        style={{ color: "var(--ink-72)", fontStyle: "italic" }}
      >
        {move.why}
      </p>

      {/* Expected result */}
      <div
        className="flex items-start gap-2 text-xs rounded-lg px-3 py-2.5"
        style={{ background: "rgba(216,168,87,0.06)", border: "1px solid rgba(216,168,87,0.18)" }}
      >
        <Target size={13} strokeWidth={1.8} style={{ color: "var(--seal)", marginTop: 1 }} />
        <div>
          <span className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: "var(--seal)" }}>
            Expected result
          </span>
          <div className="mt-0.5" data-testid={`move-expected-${index}`} style={{ color: "var(--ink)" }}>
            {move.expected_result}
          </div>
        </div>
      </div>

      {/* Actions */}
      {!isDone && !isSkipped && (
        <div className="flex items-center gap-2 mt-1">
          <button
            data-testid={`move-do-btn-${index}`}
            onClick={doAction}
            disabled={busy}
            className="btn-primary flex-1 justify-center"
          >
            {move.action?.label || "Do It"} <ArrowRight size={14} />
          </button>
          <button
            data-testid={`move-done-btn-${index}`}
            onClick={markDone}
            disabled={busy}
            className="px-3 py-2.5 rounded-lg text-xs uppercase tracking-[0.16em]"
            style={{ background: "transparent", border: "1px solid var(--ink-22)", color: "var(--ink-72)" }}
            title="Mark done"
          >
            <Check size={14} />
          </button>
          <button
            data-testid={`move-skip-btn-${index}`}
            onClick={markSkipped}
            disabled={busy}
            className="px-3 py-2.5 rounded-lg text-xs uppercase tracking-[0.16em]"
            style={{ background: "transparent", border: "1px solid var(--ink-22)", color: "var(--ink-55)" }}
            title="Skip today"
          >
            <SkipForward size={14} />
          </button>
        </div>
      )}

      {isSkipped && (
        <div
          className="text-[11px] uppercase tracking-[0.18em] text-center pt-2"
          style={{ color: "var(--ink-55)" }}
        >
          Skipped for today
        </div>
      )}

      {/* Networking log modal (inline) */}
      {showLog && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
          onClick={() => !busy && setShowLog(false)}
          data-testid="networking-log-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl p-6"
            style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
          >
            <div className="font-heading text-lg mb-1" style={{ color: "var(--ink)" }}>
              Log your outreach
            </div>
            <div className="text-xs mb-4" style={{ color: "var(--ink-55)" }}>
              How many people did you reach out to? A quick log keeps your Momentum honest.
            </div>
            <label className="block text-[10px] uppercase tracking-[0.22em] mb-1.5" style={{ color: "var(--seal)" }}>
              Count
            </label>
            <input
              type="number"
              min={1}
              value={logCount}
              onChange={(e) => setLogCount(e.target.value)}
              data-testid="networking-log-count"
              className="w-full px-3 py-2 rounded-lg text-sm mb-4"
              style={{ background: "var(--bg)", border: "1px solid var(--ink-22)", color: "var(--ink)" }}
            />
            <label className="block text-[10px] uppercase tracking-[0.22em] mb-1.5" style={{ color: "var(--seal)" }}>
              Note (optional)
            </label>
            <textarea
              value={logNote}
              onChange={(e) => setLogNote(e.target.value)}
              placeholder="Who did you reach out to?"
              rows={3}
              data-testid="networking-log-note"
              className="w-full px-3 py-2 rounded-lg text-sm mb-5"
              style={{ background: "var(--bg)", border: "1px solid var(--ink-22)", color: "var(--ink)" }}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={submitLog}
                disabled={busy}
                data-testid="networking-log-submit"
                className="btn-primary flex-1 justify-center"
              >
                Log & mark done <Check size={14} />
              </button>
              <button
                onClick={() => setShowLog(false)}
                disabled={busy}
                data-testid="networking-log-cancel"
                className="px-4 py-2.5 rounded-lg text-xs uppercase tracking-[0.16em]"
                style={{ background: "transparent", border: "1px solid var(--ink-22)", color: "var(--ink-55)" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
